import { GatsbyImage, StaticImage } from "gatsby-plugin-image";
import React, { useRef, useState } from "react";
import { CommonTranslation, useCommonTranslations } from "../components/CommonTranslations";
import { useLocale } from "../components/LanguageMenu";
import Layout from "../components/layout";
import { useRegion } from "../components/Region";
import Seo from "../components/seo";
import cx from "classnames";
import { StayInTouch } from "../components/StayinTouch";
import Amex from "../images/amex.png";
import Mastercard from "../images/mastercard.png";
import Paypal from "../images/paypal-2.png";
import Visa from "../images/visa.png";
import Background from "../images/watch-now-bg.jpg";
import Close from "../images/icons/close.svg";
import { useKeyPress } from "../utils/use-key-press";
import { AnimatePresence, motion } from "framer-motion";
import useOnClickOutside from "../utils/use-click-outside";
import Button from "../components/Button";

export default function WatchNow({ location }: { location: Location }) {
  const commonTranslations = useCommonTranslations();
  return (
    <Layout location={location} headerProps={{ fixed: true, filled: true, logo: true }}>
      <Seo title={commonTranslations.watchNow} />
      <Body />
    </Layout>
  );
}

const Body = () => {
  const { pantaflixPlayerUrl } = useRegion();
  const props = {
    pantaflixPlayerUrl,
  };

  return <WatchNowBody {...props} />;
};

interface WatchNowProps {
  pantaflixPlayerUrl: string;
}

export const WatchNowBody = ({ pantaflixPlayerUrl }: WatchNowProps) => {
  const region = useRegion();

  return pantaflixPlayerUrl ? (
    <BodyWithPlayer region={region} pantaflixPlayerUrl={pantaflixPlayerUrl} />
  ) : (
    <BodyWithoutPlayer region={region} />
  );
};

const BodyWithPlayer = ({ region, pantaflixPlayerUrl }: { region: Region; pantaflixPlayerUrl: string }) => {
  const { streamingPlatforms, partnerSitesText } = region;
  const locale = useLocale();

  const [open, setOpen] = useState(false);

  useKeyPress("Escape", () => open && setOpen(false));

  const anyStreamingPlatforms = streamingPlatforms && streamingPlatforms.length > 0;

  return (
    <div className="min-h-screen pt-20 text-white watch-now">
      {/* <PremiumBundle /> */}
      <div className="grid grid-cols-12">
        <div
          className={cx("grid w-full px-4 mb-10 sm:px-0 sm:grid-cols-8 lg:grid-cols-12 ", {
            "col-span-8 col-start-3": anyStreamingPlatforms,
            "col-span-12 sm:col-span-10 sm:col-start-2": !anyStreamingPlatforms,
          })}
        >
          <div className="flex flex-col items-center justify-center h-full col-span-12">
            <div className="overflow-hidden pt-[56.25%] h-0 relative w-full bg-[#C4C4C430] border border-white rounded-lg">
              <div className="absolute top-0 left-0 w-full h-full overflow-hidden rounded-lg">
                <iframe
                  className="w-full h-full border-0"
                  frameBorder="0"
                  src={pantaflixPlayerUrl}
                  lang={locale}
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
            <div className="flex items-center justify-end w-full mt-2 mr-1">
              <div className="flex items-center mr-2">
                <StaticImage alt="powered by pantaflix" className="w-14 md:w-24" src="../images/pantaflix.png" />
              </div>
              <button
                className="px-4 h-6 border-2 rounded-lg font-bold text-[0.6rem] flex items-center justify-center"
                onClick={() => setOpen(!open)}
              >
                <CommonTranslation value="readMore" />
              </button>
            </div>
          </div>
          <div className="col-span-12 mt-4 lg:col-span-8 lg:col-start-3 lg:pl-4 lg:mt-0">
            <p className="my-4">{partnerSitesText}</p>
            <div className="grid grid-cols-4 gap-x-2">
              {(streamingPlatforms || [])
                .filter(({ logo }) => logo.localFile?.childImageSharp.gatsbyImageData)
                .map(({ url, logo, comingSoon }) => (
                  <StreamProvider
                    url={url}
                    logo={logo.localFile.childImageSharp.gatsbyImageData}
                    comingSoon={comingSoon}
                  />
                ))}
            </div>
          </div>
        </div>
      </div>
      <AnimatePresence>
        {open && <ReadMoreModal open={open} region={region} close={() => setOpen(false)} />}
      </AnimatePresence>

      {/* <ImBusyNowBanner region={region} /> */}
    </div>
  );
};

const PremiumBundle = () => {
  return (
    <div className="flex flex-col items-center justify-center w-full py-16 mb-10 bg-primaryDark">
      <h1 className="mb-4 text-3xl font-bold text-white underline">
        WATCH OUR PREMIUM PACKAGE HERE — ONLY ON PANTAFLIX
      </h1>
      <p className="mb-2 text-xl font-semibold">Firebird feature film, plus:</p>
      <ul className="flex pl-8 list-disc gap-x-8">
        <li>introduction by Tom & Peeter</li>
        <li>deleted scenes (exclusive)</li>
        <li>45 min “making of” documentary</li>
      </ul>
      <Button className="h-10 mt-6 text-lg text-primaryDark" filled>
        Get it now
      </Button>
    </div>
  );
};

const readMoreContainerVariants = {
  hidden: {
    background: "rgba(0, 0, 0, 0)",
  },
  shown: {
    background: "rgba(0, 0, 0, 0.5)",
  },
};

const readMoreModalBodyVariants = {
  hidden: {
    opacity: 0,
  },
  shown: {
    opacity: 1,
  },
};

const ReadMoreModal = ({ region, open, close }: { region: Region; open: boolean; close: () => void }) => {
  const { pantaflixPlayerHeaderText, sellingPoints, pantaflixPlayerTVSubtext } = region;

  const ref = useRef(null);

  useOnClickOutside(ref, close);

  return (
    <motion.div
      animate={open ? "shown" : "hidden"}
      variants={readMoreContainerVariants}
      transition={{ duration: 0.5 }}
      initial="hidden"
      className="fixed top-0 z-50 flex items-center justify-center w-screen h-screen px-2"
    >
      <motion.div
        variants={readMoreModalBodyVariants}
        transition={{ duration: 0.5 }}
        ref={ref}
        className="flex flex-col px-8 bg-black border rounded-md lg:max-w-xl"
      >
        <div className="flex items-center justify-end h-20">
          <button type="button" onClick={() => close()}>
            <img className="w-10 h-10" src={Close} alt="close" />
          </button>
        </div>
        <div className="flex-grow mb-16">
          <div className="flex flex-col justify-between w-full">
            <div>
              <p className="w-full my-4 text-base font-bold leading-tight text-white xl:text-lg">
                {pantaflixPlayerHeaderText}
              </p>
              <div className="flex flex-col space-y-2">
                {sellingPoints.map(({ title, subtitle }) => (
                  <div>
                    <p className="text-sm font-bold xl:text-base">{title}</p>
                    <p className="text-xs xl:text-sm">{subtitle}</p>
                  </div>
                ))}
              </div>
              {pantaflixPlayerTVSubtext && <p className="mt-3 text-xs">{pantaflixPlayerTVSubtext}</p>}
            </div>
            <div className="flex items-center mt-4 space-x-2">
              <img src={Paypal} alt="paypal" className="w-20" />
              <img src={Visa} alt="visa" className="w-8 h-6" />
              <img src={Mastercard} alt="mastercard" className="w-8 h-6" />
              <img src={Amex} alt="amex" className="w-8 h-6" />
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const BodyWithoutPlayer = ({ region }: { region: Region }) => {
  const { streamingPlatforms, partnerSitesText } = region;

  const showStreamingPlatforms = streamingPlatforms && streamingPlatforms.length > 0;

  if (!showStreamingPlatforms) {
    return (
      <div className="grid items-center min-h-screen grid-cols-12 pt-20 text-white watch-now">
        <SignupForm region={region} />
      </div>
    );
  }

  return (
    <div className="relative flex flex-col items-center justify-center min-h-screen pt-20 pb-20 text-white watch-now">
      <div className="flex flex-col items-center justify-center w-full h-full mx-auto sm:3/4 lg:w-1/2">
        <h1 className="mb-6 text-3xl font-bold text-center uppercase sm:mb-12 sm:text-5xl text-primary">
          {partnerSitesText}
        </h1>
        <StreamingPlatforms region={region} />
      </div>
    </div>
  );
};

const StreamingPlatforms = ({ region }: { region: Region }) => {
  const { streamingPlatforms } = region;

  return (
    <div className="grid items-center justify-center grid-cols-2 px-4 md:gap-4 lg:gap-8 sm:text-center sm:grid-cols-6">
      {streamingPlatforms.map(({ url, logo }) => (
        <a href={url} className="w-full transition-transform sm:col-span-2 hover:opacity-90 hover:scale-105">
          <GatsbyImage class="w-full" alt={url} image={logo?.localFile?.childImageSharp?.gatsbyImageData} />
        </a>
      ))}
    </div>
  );
};

const SignupForm = ({ region }: { region: Region }) => {
  const { inTheatersAndOnDemand, showPlayerHeader, pantaflixPlayerHeaderText } = region;
  return (
    <div className="col-span-8 col-start-3 p-4 sm:text-center">
      {inTheatersAndOnDemand && !showPlayerHeader && (
        <p className="my-2 font-sans text-xl text-white uppercase md:my-8">{inTheatersAndOnDemand}</p>
      )}
      {showPlayerHeader && pantaflixPlayerHeaderText && (
        <p className="my-2 font-sans text-xl text-white uppercase md:my-8">{pantaflixPlayerHeaderText}</p>
      )}
      <div className="mt-12 mb-16 md:mt-0">
        <StayInTouch formClassName="flex flex-col space-y-8" withoutTitle form />
      </div>
    </div>
  );
};

const ImBusyNowBanner = ({ region }: { region: Region }) => {
  const {} = region;

  return <div className="lg:hidden w-full h-[50vh] relative bg-black">t</div>;
};

const StreamProvider = ({ url, logo, comingSoon }: { url: string; logo: any; comingSoon?: boolean }) => {
  const { comingSoon: comingSoonText } = useCommonTranslations();

  if (!comingSoon)
    return (
      <a
        href={url}
        target="_blank"
        className="flex items-center justify-center p-2 bg-black border border-white rounded-md bg-opacity-70 lg:py-4"
      >
        <GatsbyImage alt="" image={logo} objectFit="contain" className="h-7" />
      </a>
    );

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-center p-2 bg-black border border-white rounded-md bg-opacity-70 lg:py-4">
        <GatsbyImage alt="" image={logo} objectFit="contain" className="h-7 opacity-70" />
      </div>
      <h6 className="text-white font-bold uppercase text-[0.5rem] text-center opacity-70">{comingSoonText}</h6>
    </div>
  );
};
